import { useParams, useNavigate } from "react-router-dom";
import { Typography, Card, Space, Button, Spin, Empty, Tag, Descriptions, Table, message } from "antd";
import {
  FolderFilled,
  ArrowLeftOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  FileOutlined,
  DownloadOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import useSWR from "swr";
import { useState } from "react";
import { SharedWithMeResponse, FolderAccessGrant, VaultFile } from "@/types";
import { listSharedWithMe, listSharedFolderFiles, downloadFile, markFileDownloaded, SharedFolderFilesResponse } from "@/services/storageManager";
import { formatDateOnly, formatFileSize } from "@/utils/formatters";

const { Title, Text } = Typography;

export default function SharedFolderPage() {
  const { folderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState<string | null>(null);
  const [localDownloaded, setLocalDownloaded] = useState<Set<string>>(new Set());

  // Fetch shared items to find this folder's grant
  const { data, isLoading } = useSWR<SharedWithMeResponse>(
    "/access/shared-with-me",
    listSharedWithMe,
    { revalidateOnFocus: false }
  );

  // Fetch files in the shared folder
  const { data: filesData, isLoading: isLoadingFiles } = useSWR<SharedFolderFilesResponse>(
    folderId ? `/folders/${folderId}/files` : null,
    () => listSharedFolderFiles(folderId!),
    { revalidateOnFocus: false }
  );

  const folderGrant = data?.folders?.find(
    (f: FolderAccessGrant) => f.folderId === folderId
  );

  const files = filesData?.files || [];
  const serverDownloaded = new Set(folderGrant?.downloadedFileIds || []);

  const handleBack = () => {
    navigate("/share/shared-with-me");
  };

  const handleDownload = async (file: VaultFile) => {
    try {
      setDownloading(file.id);
      await downloadFile(file.id, file.fileName);

      setLocalDownloaded((prev) => new Set(prev).add(file.id));
      if (folderId) {
        markFileDownloaded(folderId, file.id).catch((err) => {
          console.error("Failed to mark file as downloaded:", err);
        });
      }
      message.success(`Downloaded ${file.fileName}`);
    } catch (error: any) {
      message.error(error.response?.data?.message || "Failed to download file");
    } finally {
      setDownloading(null);
    }
  };

  const fileColumns: ColumnsType<VaultFile> = [
    {
      title: "File",
      dataIndex: "fileName",
      key: "fileName",
      render: (fileName) => (
        <Space>
          <FileOutlined />
          <Text strong>{fileName}</Text>
        </Space>
      ),
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      width: 100,
      render: (size) => formatFileSize(size),
    },
    {
      title: "Uploaded",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => formatDateOnly(date),
    },
    {
      title: "",
      key: "actions",
      width: 160,
      render: (_, record) => {
        const isDownloaded = serverDownloaded.has(record.id) || localDownloaded.has(record.id);
        return (
          <Button
            type={isDownloaded ? "default" : "primary"}
            icon={isDownloaded ? <CheckOutlined /> : <DownloadOutlined />}
            onClick={() => handleDownload(record)}
            loading={downloading === record.id}
          >
            {isDownloaded ? "Download again" : "Download"}
          </Button>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <div style={{ padding: "24px 0", textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!folderGrant) {
    return (
      <div style={{ padding: "24px 0" }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          style={{ marginBottom: 24 }}
        >
          Back to Shared with me
        </Button>
        <Empty
          description="Folder not found or access has been revoked"
          style={{ marginTop: 80 }}
        />
      </div>
    );
  }

  const renderExpiry = () => {
    if (!folderGrant.expiresAt) {
      return <Text type="secondary">Never</Text>;
    }
    const expiresAt = new Date(folderGrant.expiresAt);
    const now = new Date();
    const daysLeft = Math.ceil(
      (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysLeft < 0) {
      return <Tag color="red">Expired</Tag>;
    } else if (daysLeft <= 3) {
      return (
        <Space>
          <ClockCircleOutlined style={{ color: "#faad14" }} />
          <Text type="warning">{daysLeft} days remaining</Text>
        </Space>
      );
    } else {
      return formatDateOnly(folderGrant.expiresAt);
    }
  };

  return (
    <div style={{ padding: "24px 0" }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={handleBack}
        style={{ marginBottom: 24 }}
      >
        Back to Shared with me
      </Button>

      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Folder header */}
          <Space align="start">
            <FolderFilled
              style={{
                fontSize: 48,
                color: folderGrant.folderColor || "#1890ff",
              }}
            />
            <div>
              <Title level={3} style={{ margin: 0 }}>
                {folderGrant.folderName}
              </Title>
              <Text type="secondary">{folderGrant.folderPath || "/"}</Text>
            </div>
          </Space>

          {/* Folder details */}
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item
              label={
                <Space>
                  <UserOutlined /> Shared by
                </Space>
              }
            >
              {folderGrant.grantedBy.name || folderGrant.grantedBy.email}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Space>
                  <CalendarOutlined /> Shared on
                </Space>
              }
            >
              {formatDateOnly(folderGrant.grantedAt)}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Space>
                  <ClockCircleOutlined /> Expires
                </Space>
              }
            >
              {renderExpiry()}
            </Descriptions.Item>
            <Descriptions.Item label="Permission">
              <Tag color="blue">{folderGrant.permission}</Tag>
            </Descriptions.Item>
          </Descriptions>
        </Space>
      </Card>

      {/* Files in folder */}
      <Card
        title={
          <Space>
            <FileOutlined />
            Files ({files.length})
          </Space>
        }
      >
        {isLoadingFiles ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : files.length === 0 ? (
          <Empty description="No files in this folder" />
        ) : (
          <Table
            columns={fileColumns}
            dataSource={files}
            rowKey="id"
            pagination={files.length > 10 ? { pageSize: 10 } : false}
          />
        )}
      </Card>
    </div>
  );
}
